import {t} from 'i18next';
import {PricingInputs} from "../../@types/pricingFormResources.tsx";
import {
    AcceptPaymentsMessagePayload,
    ConsultMessagePayload,
    PricingMessagePayload
} from "../../../shared/@types/messageResources.ts";
import {MessageConfigEnum} from "@opay/lib-core-js";
import {getFromIso2} from "../../@types/generics.tsx";
import {getUTMParameters} from "../utm/utmHelper.ts";
import {ConsultInputs} from "../../@types/consultFormResources.tsx";
import {AcceptPaymentsInputs} from "../../@types/acceptPaymentsFormResources.ts";

export const cleanUpWebsiteAddress = (websiteAddress: string) => {
    return websiteAddress.replace(/^(?:https?:\/\/)?(?:www\.)?/i, '');
};

const validateWebsiteAddress = (websiteAddress: string): boolean => {
    const regex = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/[^\s]*)?$/;
    return regex.test(websiteAddress);
};

export const handleEmptyValues = (values: any) => {
    const newValues = { ...values };
    Object.keys(newValues).forEach((key) => {
        if (newValues[key] === '') {
            newValues[key] = null;
        }
    });
    return newValues;
};

export const validatePricingFormAndReturnErrorCount = (
    data: any,
    captcha: boolean|null,
    formContainer: HTMLDivElement|null,
    setCaptcha: (arg0: boolean) => void
) => {
    let errorsCount = 0;

    if (captcha === null || !captcha) {
        setCaptcha(false);
        errorsCount += 1;
    }

    if (formContainer === null) {
        return errorsCount;
    }

    const webUrlField = formContainer.querySelector('input[name="website_address"]') as HTMLInputElement;

    if (webUrlField === null) {
        return errorsCount;
    }

    removeFieldErrors(webUrlField);

    const errorText = document.createElement('p');
    errorText.className = 'error-text';
    errorText.innerText = t('website_address_invalid');
    errorText.style.margin = '0';
    errorText.style.marginTop = '1rem';

    if (data.website_address && !validateWebsiteAddress(data.website_address)) {
        webUrlField.classList.add('error-outline');
        if (!webUrlField.parentElement?.querySelector('.error-text')) {
            webUrlField.parentElement?.appendChild(errorText);
        }

        initErrorRemoveListener(webUrlField);
        errorsCount += 1;
    }

    return errorsCount;
}

const removeFieldErrors = (formField: HTMLInputElement) => {
    formField.classList.remove('error-outline');
    formField.parentElement?.querySelector('.error-text')?.remove();
}

const initErrorRemoveListener = (formField: HTMLInputElement) => {
    formField.addEventListener('click', () => removeFieldErrors(formField));
}

export const buildPricingPayload = (data: PricingInputs, language: string, subject: string): PricingMessagePayload => {
    const utmParameters = getUTMParameters();

    return {
        config: MessageConfigEnum.STAFF_PRICING,
        language: getFromIso2(language).toUpperCase(),
        payments_per_month: data.payments_per_month,
        average_basket: data.average_basket,
        website_address: cleanUpWebsiteAddress(data.website_address),
        email: data.email.toLowerCase(),
        phone_number: data.phone_number,
        special_code: data.special_code === null ? '-' : data.special_code,
        website_content_management_system: t(data.website_content_management_system),
        subject: `${t(subject)} | ${data.website_address !== null ? data.website_address : data.email}`,
        utm_source: utmParameters.utm_source,
        utm_medium: utmParameters.utm_medium,
        utm_campaign: utmParameters.utm_campaign,
        utm_term: utmParameters.utm_term,
        utm_content: utmParameters.utm_content
    }
}

export const buildConsultPayload = (data: ConsultInputs, language: string): ConsultMessagePayload => {
    const utmParameters = getUTMParameters();

    return {
        config: MessageConfigEnum.STAFF_CONSULT,
        language: getFromIso2(language).toUpperCase(),
        name: data.name,
        email: data.email.toLowerCase(),
        website_address: data.website_address !== null ? cleanUpWebsiteAddress(data.website_address) : null,
        phone_number: data.phone_number,
        subject: `${t(data.subject)} | ${data.website_address !== null ? data.website_address : data.email}`,
        user_subject: t(data.subject),
        message: data.message,
        utm_source: utmParameters.utm_source,
        utm_medium: utmParameters.utm_medium,
        utm_campaign: utmParameters.utm_campaign,
        utm_term: utmParameters.utm_term,
        utm_content: utmParameters.utm_content
    }
}

export const buildAcceptPaymentsPayload = (data: AcceptPaymentsInputs): AcceptPaymentsMessagePayload => {
    const utmParameters = getUTMParameters();
    const website = data.website_url
        ? cleanUpWebsiteAddress(data.website_url)
        : data.website_check_url ? cleanUpWebsiteAddress(data.website_check_url) : '';

    return {
        config: MessageConfigEnum.STAFF_ACCEPT_PAYMENTS,
        language: data.language,
        website_address: website,
        subject: `Nauja registracija | ${website}`,
        email: 'sales@opay.eu',
        ...utmParameters
    }
}
